import React from "react";
import { Link } from "react-router-dom";
import { Calendar, ClipboardList, MessageCircle, Syringe, Heart,
         Clock, CalendarDays, BarChart3, Gem,
         Droplet, Dumbbell, Sparkles, MessagesSquare } from "lucide-react";
import Header from "@/components/Header";
import "./About.css";

// Five-stage journey — desktop shows 1→2→3→4→5 with arrows, mobile stacks
// as a vertical timeline (see About.css).
const JOURNEY = [
  { n: "01", Icon: Calendar,        title: "Book Your Appointment",
    body: "Choose your treatment and a time that suits you via our online booking system. A deposit is required to secure your appointment." },
  { n: "02", Icon: ClipboardList,   title: "Before Your Appointment",
    body: "You will receive a confirmation email with important information, including treatment guidelines and a consent form to complete online." },
  { n: "03", Icon: MessageCircle,   title: "Consultation",
    body: "We will discuss your goals, medical history and suitability to create a personalised treatment plan for you." },
  { n: "04", Icon: Syringe,         title: "Your Treatment",
    body: "Your treatment will be carried out in a safe, comfortable environment using high-quality, clinically approved products and techniques." },
  { n: "05", Icon: Heart,           title: "Aftercare & Support",
    body: "You will be given full aftercare advice and ongoing support to help you achieve the best and longest-lasting results." },
];

const TIMELINE = [
  { Icon: Clock,        title: "0 – 24 Hours", body: "Mild redness, swelling or tenderness may occur." },
  { Icon: CalendarDays, title: "2 – 7 Days",   body: "Side effects typically subside and your skin begins to settle." },
  { Icon: BarChart3,    title: "1 – 2 Weeks",  body: "Initial results become visible (depending on treatment) and continue to improve." },
  { Icon: Gem,          title: "Longer Term",  body: "Results continue to develop and maintenance treatments may be recommended depending on the treatment." },
];

const AFTERCARE = [
  { Icon: Droplet,          title: "Keep Hydrated",
    body: "Drink plenty of water to support healing and optimal results." },
  { Icon: Dumbbell,         title: "Avoid Heat & Exercise",
    body: "Avoid intense exercise, saunas, hot baths and direct sun exposure for the time advised in your treatment-specific aftercare." },
  { Icon: Sparkles,         title: "Follow Aftercare Advice",
    body: "Follow the personalised aftercare instructions provided following your treatment." },
  { Icon: MessagesSquare,   title: "We're Here For You",
    body: "If you have any questions or concerns, please get in touch." },
];

export default function About() {
  return (
    <div className="clae-home" data-testid="about-page">
      <Header />
      <section className="clae-page">
        <p className="clae-eyebrow">ABOUT CLAÈ</p>
        <h1 className="clae-h1 clae-h1--sm">ENHANCE,<br />NEVER OVERWHELM.</h1>
        <div className="clae-page__grid">
          <p className="clae-body">
            CLAÈ Aesthetics is a private London studio dedicated to refined,
            natural-looking aesthetics. Every treatment begins with a considered
            consultation — understanding your features, your goals, and the
            balance that already exists on your face.
          </p>
          <p className="clae-body">
            Our philosophy is simple: enhance what is already there, never
            replace it. The result is a look that feels quietly considered
            rather than obviously done — the mark of a CLAÈ appointment.
          </p>
        </div>

        {/* ── Meet Claudia (above The CLAÈ Journey) ───────────── */}
        <section className="clae-meet" data-testid="clae-meet-section" aria-labelledby="meet-claudia-title">
          <div className="clae-meet__text">
            <p className="clae-meet__eyebrow">FOUNDER &amp; AESTHETIC PRACTITIONER</p>
            <h2 id="meet-claudia-title" className="clae-meet__title">Meet Claudia</h2>
            <div className="clae-meet__rule" aria-hidden="true" />
            <p className="clae-body">
              CLAÈ was founded by Claudia with a passion for creating a more personal,
              refined approach to aesthetics — helping clients feel confident through
              subtle, natural-looking results.
            </p>
            <p className="clae-body clae-meet__signoff">Welcome to CLAÈ.</p>
          </div>
          <div className="clae-meet__portrait" aria-hidden="true">
            <img
              src="/claudia-portrait.png"
              alt="Claudia, Founder of CLAÈ"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement?.classList.add("clae-meet__portrait--empty");
              }}
            />
            <span className="clae-meet__portrait-fallback">C</span>
          </div>
        </section>

        {/* ── The CLAÈ Journey ─────────────────────────────────── */}
        <section className="clae-journey" data-testid="clae-journey-section" aria-labelledby="journey-title">
          <header className="clae-journey__header">
            <h2 id="journey-title" className="clae-journey__title">The CLAÈ Journey</h2>
            <div className="clae-journey__rule" aria-hidden="true" />
            <p className="clae-journey__eyebrow">FROM BOOKING TO LASTING RESULTS</p>
          </header>
          <ol className="clae-journey__grid" data-testid="clae-journey-steps">
            {JOURNEY.map(({ n, Icon, title, body }, i) => (
              <React.Fragment key={n}>
                <li className="clae-journey__card" data-testid={`journey-step-${i + 1}`}>
                  <div className="clae-journey__num" aria-hidden="true">{n}</div>
                  <div className="clae-journey__icon" aria-hidden="true"><Icon size={22} strokeWidth={1.2} /></div>
                  <h3 className="clae-journey__card-title">{title}</h3>
                  <p className="clae-journey__body">{body}</p>
                </li>
                {i < JOURNEY.length - 1 && (
                  <li className="clae-journey__arrow" aria-hidden="true">→</li>
                )}
              </React.Fragment>
            ))}
          </ol>
        </section>

        {/* ── Results Timeline ─────────────────────────────────── */}
        <section className="clae-timeline" data-testid="clae-timeline-section" aria-labelledby="timeline-title">
          <h2 id="timeline-title" className="clae-timeline__title">Your Results Timeline</h2>
          <div className="clae-journey__rule" aria-hidden="true" />
          <ol className="clae-timeline__grid" data-testid="clae-timeline-steps">
            {TIMELINE.map(({ Icon, title, body }, i) => (
              <li key={title} className="clae-timeline__card" data-testid={`timeline-step-${i + 1}`}>
                <div className="clae-timeline__icon" aria-hidden="true"><Icon size={20} strokeWidth={1.2} /></div>
                <h3 className="clae-timeline__card-title">{title}</h3>
                <span className="clae-timeline__mark" aria-hidden="true" />
                <p className="clae-journey__body">{body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Aftercare ────────────────────────────────────────── */}
        <section className="clae-aftercare-band" data-testid="clae-aftercare-section" aria-labelledby="aftercare-title">
          <h2 id="aftercare-title" className="clae-timeline__title">Aftercare</h2>
          <div className="clae-journey__rule" aria-hidden="true" />
          <p className="clae-aftercare-band__sub">SIMPLE STEPS FOR THE BEST RESULTS</p>
          <ol className="clae-aftercare-band__grid" data-testid="clae-aftercare-steps">
            {AFTERCARE.map(({ Icon, title, body }, i) => (
              <li key={title} className="clae-aftercare-band__card" data-testid={`aftercare-step-${i + 1}`}>
                <div className="clae-aftercare-band__icon" aria-hidden="true"><Icon size={22} strokeWidth={1.2} /></div>
                <h3 className="clae-timeline__card-title">{title}</h3>
                <span className="clae-timeline__mark" aria-hidden="true" />
                <p className="clae-journey__body">{body}</p>
              </li>
            ))}
          </ol>
          <p className="clae-aftercare-band__note">
            Where treatment-specific aftercare differs, the wording above does not
            override the individual aftercare instructions provided to you.
          </p>
        </section>

        {/* ── CTA ──────────────────────────────────────────────── */}
        <div className="clae-journey__cta" data-testid="clae-journey-cta">
          <p className="clae-journey__cta-eyebrow">READY TO BEGIN YOUR CLAÈ JOURNEY?</p>
          <Link
            to="/treatments"
            className="clae-btn clae-btn--primary"
            data-testid="about-cta"
          >BOOK YOUR CONSULTATION</Link>
        </div>
      </section>
    </div>
  );
}
